<template>
  <b-modal
    id="modal-historico-atendimento"
    :visible="visivel"
    size="lg"
    :title="modalTitle"
    scrollable
    hide-footer
    @hide="$emit('update:visivel', false)"
  >
    <!-- MODO CONTATO: Lista de atendimentos -->
    <div
      v-if="historico && historico.contato && historico.atendimentos"
      class="contact-info mb-4"
    >
      <div class="d-flex align-items-center mb-3">
        <div class="avatar-contato mr-3">
          <b-avatar
            :src="avatarUrl(historico.contato.name)"
            size="40px"
          />
        </div>
        <div>
          <h5 class="mb-1">
            {{ historico.contato.name }}
          </h5>
          <p class="text-muted mb-0">
            ID: {{ historico.contato.id }} | {{ historico.contato.phone }}
          </p>
        </div>
      </div>

      <!-- Botão para iniciar nova conversa se não houver atendimento em andamento -->
      <div
        v-if="!temAtendimentoEmAndamento"
        class="text-center mb-4"
      >
        <button
          class="btn btn-primary btn-lg"
          :disabled="loadingConversa"
          @click="iniciarConversa"
        >
          <i
            v-if="loadingConversa"
            class="fas fa-spinner fa-spin mr-2"
          />
          <i
            v-else
            class="fas fa-comments mr-2"
          />
          {{ loadingConversa ? 'Abrindo...' : 'Iniciar Conversa' }}
        </button>
      </div>

      <div class="attendances-list">
        <h6 class="mb-3">
          Histórico de Atendimentos ({{ historico.atendimentos.length }})
        </h6>
        <div class="list-group">
          <div
            v-for="(atendimento, index) in historico.atendimentos"
            :key="atendimento.id"
            class="list-group-item list-group-item-action"
            :class="{ 'active': atendimentoAtivo === index }"
            @click="carregarAtendimento(atendimento, index)"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="d-flex align-items-center mb-1">
                  <span class="badge-info mr-2">Atendimento {{ index + 1 }}</span>
                  <span :class="['badge', getStatusBadgeClass(atendimento.status)]">
                    {{ getStatusLabel(atendimento.status) }}
                  </span>
                </div>
                <div class="text-muted small">
                  <div>Início: {{ formatDate(atendimento.dataInicio) }}</div>
                  <div v-if="atendimento.dataFim">
                    Fim: {{ formatDate(atendimento.dataFim) }}
                  </div>
                  <div
                    v-if="atendimento.dataFim"
                    class="mt-1"
                  >
                    <strong>Duração:</strong> {{ getDuration(atendimento.dataInicio, atendimento.dataFim) }}
                  </div>
                </div>
              </div>
              <div class="text-right">
                <div
                  v-if="atendimento.atendimentoBot"
                  class="badge badge-secondary"
                >
                  Bot
                </div>
                <div
                  v-else
                  class="badge badge-primary"
                >
                  Humano
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODO ATENDIMENTO: Mensagens do atendimento selecionado -->
    <div
      v-if="historico && historico.mensagens && historico.mensagens.length > 0"
      class="messages-section"
    >
      <b-alert
        v-if="historico.emAndamento"
        show
        variant="warning"
        class="d-flex align-items-center mb-3"
      >
        <feather-icon
          icon="InfoIcon"
          size="20"
          class="mr-2 flex-shrink-0"
        />
        <span>Este é o histórico da conversa até o momento. As mensagens não atualizam em tempo real.</span>
      </b-alert>

      <div
        v-if="historico && historico.contato"
        class="d-flex justify-content-between align-items-center mb-3"
      >
        <h6 class="mb-0">
          Mensagens do Atendimento
        </h6>
        <span class="badge badge-info">Atendimento {{ atendimentoAtivo + 1 }}</span>
      </div>

      <!-- Resumo de atendentes (quando houve transferência) -->
      <div v-if="houveTransferencia" class="resumo-atendentes mb-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <span class="badge badge-primary">
            <i class="feather icon-users mr-1"></i>
            {{ atendentesParticipantes.length }} atendentes
          </span>
          <span 
            v-for="(atendente) in atendentesParticipantes" 
            :key="atendente.id"
            class="badge badge-atendente"
          >
            {{ atendente.nome }}
          </span>
        </div>
      </div>

      <div class="chat-historico px-2 py-1">
        <template v-for="(mensagem, index) in historico.mensagens">
          <!-- Marcador de troca de atendente -->
          <SeparadorTrocaAtendente
            v-if="mensagem.tipo === 'troca_atendente'"
            :key="mensagem.mensagemId || `troca-${index}`"
            :atendente-anterior="mensagem.atendente_anterior"
            :atendente-novo="mensagem.atendente_novo"
            :timestamp="mensagem.timestamp"
          />

          <!-- Mensagem normal -->
          <div
            v-else
            :key="mensagem._id || index"
            class="mb-3 d-flex modal-historico-msg-row"
            :class="mensagem.remetente === 'cliente' ? 'justify-content-start' : 'justify-content-end'"
            :data-whatsapp-id="mensagem.whatsapp_id || undefined"
          >
            <div
              class="chat-bubble"
              :class="`bubble-${mensagem.remetente}`"
            >
              <!-- Bot -->
              <div
                v-if="mensagem.remetente === 'bot'"
                class="bot-meta text-right text-muted small mb-1"
              >
                🤖 Assistente
              </div>

              <!-- IMAGEM com clique -->
              <template v-if="mensagem.tipo === 'image' && mensagem.url">
                <img
                  :src="mensagem.url"
                  alt="imagem"
                  class="img-msg-preview img-fluid rounded"
                  style="cursor: zoom-in;"
                  @click="abrirMidia(mensagem)"
                >
                <div
                  v-if="mensagem.texto"
                  class="mt-1"
                >
                  {{ mensagem.texto }}
                </div>
              </template>

              <!-- VÍDEO -->
              <template v-else-if="mensagem.tipo === 'video' && mensagem.url">
                <video
                  :src="mensagem.url"
                  controls
                  class="rounded"
                  style="max-width: 100%; max-height: 300px;"
                />
                <div
                  v-if="mensagem.texto"
                  class="mt-1"
                >
                  {{ mensagem.texto }}
                </div>
              </template>

              <!-- ÁUDIO (customizado) -->
              <template v-else-if="mensagem.tipo === 'audio' && mensagem.url">
                <div class="audio-player-ui mt-1">
                  <button
                    class="btn-audio-icon"
                    @click="togglePlayAudio(mensagem)"
                  >
                    <feather-icon
                      :icon="mensagem._playing ? 'PauseIcon' : 'PlayIcon'"
                      size="16"
                    />
                  </button>
                  <span class="audio-time">{{ formatTime(mensagem._currentTime || 0) }}</span>
                  <div class="audio-progress-container">
                    <div
                      class="audio-progress-bar"
                      :style="{ width: (mensagem._progress || 0) + '%' }"
                    />
                  </div>
                  <span class="audio-time">{{ formatTime(mensagem._duration || 0) }}</span>
                  <button
                    class="btn-audio-speed"
                    @click="alternarVelocidadeAudio"
                  >
                    {{ velocidadeAudio.toFixed(1) }}x
                  </button>
                </div>
                <div
                  v-if="mensagem.texto"
                  class="mt-1"
                >
                  {{ mensagem.texto }}
                </div>
              </template>

              <!-- DOCUMENTO -->
              <template v-else-if="['pdf', 'document'].includes(mensagem.tipo) && mensagem.url">
                <a
                  :href="mensagem.url"
                  target="_blank"
                  rel="noopener"
                  class="text-white"
                >📄 Visualizar documento</a>
                <div
                  v-if="mensagem.texto"
                  class="mt-1"
                >
                  {{ mensagem.texto }}
                </div>
              </template>

              <!-- TEXTO -->
              <template v-else>
                <div style="white-space: pre-line;">
                  {{ mensagem.texto }}
                </div>
              </template>

              <div class="bot-timestamp text-muted small text-right mt-1">
                {{ formatDate(mensagem.timestamp) }}
              </div>
            </div>
          </div>
        </template>

        <!-- Marcador de atendimento resolvido (no final) -->
        <SeparadorAtendimentoResolvido
          v-if="atendimentoResolvido && dadosResolucao"
          :atendente-nome="dadosResolucao.atendenteNome"
          :timestamp="dadosResolucao.timestamp"
        />
      </div>
    </div>

    <!-- Sem mensagens -->
    <div
      v-else-if="!historico || !historico.mensagens || !historico.mensagens.length"
      class="text-center text-muted py-4"
    >
      <b-alert
        v-if="historico && historico.emAndamento"
        show
        variant="warning"
        class="d-flex align-items-center mb-3 text-left"
      >
        <feather-icon
          icon="InfoIcon"
          size="20"
          class="mr-2 flex-shrink-0"
        />
        <span>Este é o histórico da conversa até o momento. As mensagens não atualizam em tempo real.</span>
      </b-alert>
      <div v-if="historico && historico.contato">
        Selecione um atendimento para ver as mensagens.
      </div>
      <div v-else>
        Nenhuma mensagem encontrada neste atendimento.
      </div>
    </div>

    <!-- Modal de mídia (imagem ou vídeo) -->
    <modal-midia
      :midia="midiaSelecionada"
      :visivel="modalMidiaVisivel"
      @fechar="modalMidiaVisivel = false"
    />

    <!-- Modal para nova conversa foi movido para o componente pai (Search.vue) -->
  </b-modal>
</template>

<script>
import ModalMidia from './ModalMidia.vue'
import SeparadorTrocaAtendente from './components/SeparadorTrocaAtendente.vue'
import SeparadorAtendimentoResolvido from './components/SeparadorAtendimentoResolvido.vue'
import { getChatOfContact } from '../../functions/basics/chat'
import { inserirMarcadoresTroca } from '@/utils/chat-helpers'

export default {
  name: 'ModalHistoricoAtendimento',
  components: {
    ModalMidia,
    SeparadorTrocaAtendente,
    SeparadorAtendimentoResolvido,
  },
  props: {
    visivel: Boolean,
    historico: {
      type: Object,
      default: () => ({ mensagens: [] }),
    },
    historicoAtendentes: {
      type: Array,
      default: () => []
    },
    atendentes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      velocidadeAudio: 1.0,
      modalMidiaVisivel: false,
      midiaSelecionada: null,
      atendimentoAtivo: null,
      loadingConversa: false,
      mensagensProcessadas: false,
    }
  },
  watch: {
    visivel: {
      immediate: true,
      handler(novoValor) {
        if (novoValor) {
          this.mensagensProcessadas = false
          this.$nextTick(() => {
            this.processarMensagensSeNecessario()
          })
        }
      }
    },
    'historico.mensagens': {
      handler() {
        if (this.visivel && !this.mensagensProcessadas) {
          this.$nextTick(() => {
            this.processarMensagensSeNecessario()
          })
        }
      }
    }
  },
  computed: {
    modalTitle() {
      if (this.historico && this.historico.contato && this.historico.contato.name) {
        return `Histórico de Atendimentos - ${this.historico.contato.name}`
      }
      return 'Histórico do Atendimento'
    },

    temAtendimentoEmAndamento() {
      if (!this.historico || !this.historico.atendimentos || !this.historico.atendimentos.length) {
        return false
      }
      return this.historico.atendimentos.some(atendimento => atendimento.status === 'em_andamento')
    },

    atendentesParticipantes() {
      const historico = this.historico?.historicoAtendentes || this.historico?.historico_atendentes || this.historicoAtendentes || []
      if (!historico || historico.length === 0) {
        return []
      }
      const unicos = []
      const ids = new Set()
      for (const h of historico) {
        const id = h.atendente_id || h.id
        if (!ids.has(id)) {
          ids.add(id)
          // Buscar nome na lista de atendentes se não veio da API
          let nome = h.atendente_nome || h.nome
          if (!nome && this.atendentes && this.atendentes.length > 0) {
            const atendente = this.atendentes.find(a => (a.value || a.id) === id)
            nome = atendente?.text || atendente?.name
          }
          unicos.push({
            id: id,
            nome: nome || 'Atendente'
          })
        }
      }
      return unicos
    },

    houveTransferencia() {
      return this.atendentesParticipantes.length > 1
    },

    atendimentoResolvido() {
      const status = this.historico?.status
      const fim = this.historico?.fim || this.historico?.dataFim
      return status === 'resolvido' || (fim && !this.historico?.emAndamento)
    },

    dadosResolucao() {
      if (!this.atendimentoResolvido) return null
      
      const historico = this.historico?.historicoAtendentes || this.historico?.historico_atendentes || this.historicoAtendentes || []
      const fim = this.historico?.fim || this.historico?.dataFim
      
      if (historico.length > 0) {
        const ultimo = historico[historico.length - 1]
        let nome = ultimo.atendente_nome || ultimo.nome
        
        if (!nome && this.atendentes && this.atendentes.length > 0) {
          const id = ultimo.atendente_id || ultimo.id
          const atendente = this.atendentes.find(a => (a.value || a.id) === id)
          nome = atendente?.text || atendente?.name
        }
        
        return {
          atendenteNome: nome || 'Atendente',
          timestamp: fim || ultimo.data_fim || ultimo.fim || Date.now()
        }
      }
      
      return {
        atendenteNome: 'Atendente',
        timestamp: fim || Date.now()
      }
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatTime(segundos) {
      const m = Math.floor(segundos / 60).toString().padStart(1, '0')
      const s = Math.floor(segundos % 60).toString().padStart(2, '0')
      return `${m}:${s}`
    },
    togglePlayAudio(msg) {
      if (!msg._audio) {
        this.$set(msg, '_progress', 0)
        this.$set(msg, '_playing', false)
        this.$set(msg, '_audio', new Audio(msg.url))
        this.$set(msg, '_currentTime', 0)
        this.$set(msg, '_duration', 0)

        msg._audio.preload = 'auto'
        msg._audio.addEventListener('loadedmetadata', () => {
          msg._duration = msg._audio.duration
        })
        msg._audio.addEventListener('timeupdate', () => {
          msg._currentTime = msg._audio.currentTime
          msg._progress = msg._audio.duration
            ? (msg._audio.currentTime / msg._audio.duration) * 100
            : 0
        })
        msg._audio.addEventListener('ended', () => {
          msg._playing = false
          msg._currentTime = 0
          msg._progress = 0
        })
      }

      if (msg._playing) {
        msg._audio.pause()
        msg._playing = false
      } else {
        this.pausarTodosAudios(msg)
        msg._audio.playbackRate = this.velocidadeAudio
        msg._audio.play()
        msg._playing = true
      }
    },
    pausarTodosAudios(audioAtivo) {
      if (!this.historico || !this.historico.mensagens) return
      this.historico.mensagens.forEach(msg => {
        if (msg._audio && msg !== audioAtivo) {
          msg._audio.pause()
          msg._playing = false
        }
      })
    },
    alternarVelocidadeAudio() {
      const velocidades = [1.0, 1.2, 1.5, 2.0]
      const atual = velocidades.indexOf(this.velocidadeAudio)
      this.velocidadeAudio = velocidades[(atual + 1) % velocidades.length]

      if (!this.historico || !this.historico.mensagens) return
      this.historico.mensagens.forEach(msg => {
        if (msg._audio) {
          msg._audio.playbackRate = this.velocidadeAudio
        }
      })
    },
    abrirMidia(midia) {
      this.midiaSelecionada = midia
      this.modalMidiaVisivel = true
    },

    // NOVOS MÉTODOS PARA CONTATO
    avatarUrl(nome) {
      const seed = encodeURIComponent(nome || 'user')
      return `https://api.dicebear.com/7.x/thumbs/svg?seed=${seed}`
    },

    getStatusLabel(status) {
      const labels = {
        em_andamento: 'Em Andamento',
        resolvido: 'Resolvido',
        pendente: 'Pendente',
        cancelado: 'Cancelado',
      }
      return labels[status] || status
    },

    getStatusBadgeClass(status) {
      const classes = {
        em_andamento: 'badge-warning',
        resolvido: 'badge-success',
        pendente: 'badge-info',
        cancelado: 'badge-danger',
      }
      return classes[status] || 'badge-secondary'
    },

    getDuration(dataInicio, dataFim) {
      if (!dataInicio || !dataFim) return 'N/A'

      const inicio = new Date(dataInicio)
      const fim = new Date(dataFim)
      const diffMs = fim - inicio

      const horas = Math.floor(diffMs / (1000 * 60 * 60))
      const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

      if (horas > 0) {
        return `${horas}h ${minutos}m`
      }
      return `${minutos}m`
    },

    async carregarAtendimento(atendimento, index) {
      this.atendimentoAtivo = index

      try {
        // Carregar mensagens do atendimento
        const resultado = await getChatOfContact(atendimento.id)
        
        // API pode retornar objeto { mensagens, historico_atendentes } ou array direto
        const mensagens = resultado.mensagens || resultado || []

        // Processar mensagens com marcadores de transferência
        let mensagensProcessadas = mensagens

        // Usar historicoAtendentes da API, do atendimento ou da prop
        let historicoAtendentesFinal = resultado.historico_atendentes || atendimento.historico_atendentes || this.historicoAtendentes || []
        
        // Enriquecer com nomes se não vieram da API
        if (historicoAtendentesFinal.length > 0 && this.atendentes && this.atendentes.length > 0) {
          historicoAtendentesFinal = historicoAtendentesFinal.map(h => {
            if (h.atendente_nome && h.atendente_nome !== 'Atendente') return h
            const id = h.atendente_id || h.id
            const atendente = this.atendentes.find(a => (a.value || a.id) === id)
            return {
              ...h,
              atendente_nome: h.atendente_nome || atendente?.text || atendente?.name || 'Atendente'
            }
          })
        }
        
        if (historicoAtendentesFinal && historicoAtendentesFinal.length > 0) {
          mensagensProcessadas = inserirMarcadoresTroca(mensagens, historicoAtendentesFinal)
        }

        // Atualizar o histórico com as mensagens processadas
        if (this.historico) {
          this.$set(this.historico, 'mensagens', mensagensProcessadas)
        }

        this.mensagensProcessadas = false
        this.$nextTick(() => {
          this.processarMensagensSeNecessario()
        })

        // Scroll automático até o histórico da conversa
        this.$nextTick(() => {
          // Aguardar renderização das mensagens
          setTimeout(() => {
            this.fazerScrollParaMensagens()
          }, 300)

          // Tentativa adicional após mais tempo para garantir
          setTimeout(() => {
            this.fazerScrollParaMensagens()
          }, 800)
        })
      } catch (error) {
        console.error('Erro ao carregar atendimento:', error)
        this.$toast.error('Erro ao carregar mensagens do atendimento.')
      }
    },

    // MÉTODOS PARA NOVA CONVERSA
    async iniciarConversa() {
      if (!this.historico || !this.historico.contato) return

      this.loadingConversa = true

      try {
        // Preparar dados do contato
        const dadosContato = {
          id: this.historico.contato.id,
          name: this.historico.contato.name,
          phone: this.historico.contato.phone,
        }

        // Notificar o componente pai para abrir o modal de nova conversa
        this.$emit('iniciar-conversa', dadosContato)
        // Fechar este modal de histórico
        this.$emit('update:visivel', false)
      } catch (error) {
        console.error('Erro ao iniciar conversa:', error)
        this.$toast.error('Erro ao abrir nova conversa.')
      } finally {
        this.loadingConversa = false
      }
    },

    abrirConversaExistente(conversa) {
      // Emitir evento para o componente pai (Search.vue)
      this.$emit('abrir-conversa-existente', conversa)
    },

    processarMensagensSeNecessario() {
      if (this.mensagensProcessadas) return
      if (!this.historico || !this.historico.mensagens || this.historico.mensagens.length === 0) return
      
      let historicoAtendentesFinal = this.historico?.historicoAtendentes || this.historico?.historico_atendentes || this.historicoAtendentes || []
      
      // Enriquecer com nomes se não vieram da API
      if (historicoAtendentesFinal.length > 0 && this.atendentes && this.atendentes.length > 0) {
        historicoAtendentesFinal = historicoAtendentesFinal.map(h => {
          if (h.atendente_nome && h.atendente_nome !== 'Atendente') return h
          const id = h.atendente_id || h.id
          const atendente = this.atendentes.find(a => (a.value || a.id) === id)
          return {
            ...h,
            atendente_nome: h.atendente_nome || atendente?.text || atendente?.name || 'Atendente'
          }
        })
      }
      
      if (historicoAtendentesFinal && historicoAtendentesFinal.length > 0) {
        const mensagensOriginais = this.historico.mensagens
        const jaPossuiMarcadores = mensagensOriginais.some(m => m.tipo === 'troca_atendente')
        
        if (!jaPossuiMarcadores) {
          const mensagensComMarcadores = inserirMarcadoresTroca(mensagensOriginais, historicoAtendentesFinal)
          this.$set(this.historico, 'mensagens', mensagensComMarcadores)
        }
      }
      this.mensagensProcessadas = true
      this.agendarDestaqueReply()
    },

    agendarDestaqueReply() {
      if (!this.historico || !this.historico.waIdDestaque || !this.visivel) return
      this.$nextTick(() => {
        setTimeout(() => this.aplicarDestaqueReply(), 350)
        setTimeout(() => this.aplicarDestaqueReply(), 850)
      })
    },

    aplicarDestaqueReply() {
      const wa = this.historico && this.historico.waIdDestaque
      if (!wa || !this.visivel) return
      const waNorm = String(wa).trim()
      const modalRoot = document.getElementById('modal-historico-atendimento')
      if (!modalRoot) return

      let alvo = null
      const nodes = modalRoot.querySelectorAll('.modal-historico-msg-row[data-whatsapp-id]')
      nodes.forEach((node) => {
        const v = (node.getAttribute('data-whatsapp-id') || '').trim()
        if (v === waNorm) alvo = node
      })
      if (!alvo) return

      alvo.scrollIntoView({ behavior: 'smooth', block: 'center' })
      alvo.classList.add('mensagem-destacada-historico')
      setTimeout(() => {
        alvo.classList.remove('mensagem-destacada-historico')
      }, 2600)
    },

    fazerScrollParaMensagens() {
      try {
        // Tentar diferentes seletores para encontrar o modal
        let modalElement = null

        // Estratégia 1: Buscar pelo ID do modal
        modalElement = document.getElementById('modal-historico-atendimento')

        // Estratégia 2: Buscar por classe do modal
        if (!modalElement) {
          modalElement = document.querySelector('.modal.show')
        }

        // Estratégia 3: Buscar pelo body do modal
        if (!modalElement) {
          modalElement = document.querySelector('.modal-body')
        }

        if (modalElement) {
          // Buscar a seção de mensagens dentro do modal
          const messagesSection = modalElement.querySelector('.messages-section')
          if (messagesSection) {
            messagesSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
            return
          }

          // Fallback: scroll para o chat-historico
          const chatHistorico = modalElement.querySelector('.chat-historico')
          if (chatHistorico) {
            chatHistorico.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
            return
          }

          // Último fallback: scroll para o final do modal
          const modalBody = modalElement.querySelector('.modal-body')
          if (modalBody) {
            modalBody.scrollTop = modalBody.scrollHeight
          }
        }
      } catch (error) {
        console.error('Erro ao fazer scroll:', error)
      }
    },
  },
}
</script>

<style scoped>
.chat-historico {
  max-height: 500px;
  overflow-y: auto;
  background-color: #f5f0f5;
  border-radius: 8px;
}

.modal-historico-msg-row.mensagem-destacada-historico {
  animation: mcHistoricoDestaque 2.2s ease-in-out;
  border-radius: 10px;
  outline: 2px solid rgba(124, 58, 237, 0.85);
  outline-offset: 2px;
}

@keyframes mcHistoricoDestaque {
  0% {
    background-color: rgba(124, 58, 237, 0.18);
    box-shadow: 0 0 14px rgba(124, 58, 237, 0.4);
  }
  100% {
    background-color: transparent;
    box-shadow: none;
  }
}

.chat-bubble {
  display: inline-block;
  max-width: 65%;
  padding: 6px 9px 8px 9px;
  border-radius: 12px;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 0.9rem;
  line-height: 1.4;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  box-shadow: none;
  position: relative;
}

@media (max-width: 768px) {
  .chat-bubble {
    max-width: 85%;
  }
}

.bubble-cliente {
  background-color: #ffffff;
  color: #333;
  border-radius: 0 7.5px 7.5px 7.5px;
}

.bubble-cliente::before {
  content: '';
  position: absolute;
  top: 0;
  left: -8px;
  width: 8px;
  height: 13px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13'%3E%3Cpath fill='%23ffffff' d='M2.812,0H8v11.193L1.533,2.568C0.474,1.156,1.042,0,2.812,0z'/%3E%3C/svg%3E");
  background-size: 8px 13px;
  background-repeat: no-repeat;
}

.bubble-atendente {
  background-color: #9a30912d;
  color: #333;
  border-radius: 7.5px 0 7.5px 7.5px;
}

.bubble-atendente::after {
  content: '';
  position: absolute;
  top: 0;
  right: -8px;
  width: 8px;
  height: 13px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13'%3E%3Cpath fill='%239a30912d' d='M5.188,0H0v11.193l6.467-8.625C7.526,1.156,6.958,0,5.188,0z'/%3E%3C/svg%3E");
  background-size: 8px 13px;
  background-repeat: no-repeat;
}

.bubble-bot {
  background-color: #fff8e1;
  color: #333;
  border-left: 3px solid #FFA000;
  border-radius: 12px;
}

.bubble-bot::before,
.bubble-bot::after {
  display: none;
}

.bot-meta {
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 2px;
}

.bot-timestamp {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.45);
}

.bubble-atendente .bot-timestamp,
.bubble-bot .bot-timestamp {
  text-align: right;
}

.img-msg-preview {
  max-width: 250px;
  border-radius: 8px;
}
.audio-player-ui {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 12px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.btn-audio-icon,
.btn-audio-speed {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
}
.audio-time {
  font-size: 0.75rem;
  min-width: 35px;
  text-align: center;
}
.audio-progress-container {
  flex: 1;
  height: 4px;
  background-color: #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}
.audio-progress-bar {
  background-color: #7367F0;
  height: 100%;
  width: 0%;
  transition: width 0.2s ease-in-out;
}

/* ESTILOS PARA CONTATO */
.contact-info {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 1rem;
}

.attendances-list {
  margin-top: 1rem;
}

.attendances-list .list-group-item {
  cursor: pointer;
  transition: all 0.2s ease;
}

.attendances-list .list-group-item:hover {
  background-color: #f8f9fa;
  transform: translateX(2px);
}

.attendances-list .list-group-item.active {
  background-color: #e3f2fd;
  border-color: #2196f3;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.messages-section {
  padding-top: 1rem;
}

/* Modo escuro */
body.dark-layout .contact-info {
  border-bottom-color: #495057;
}

body.dark-layout .attendances-list .list-group-item {
  background-color: #343a40;
  border-color: #495057;
  color: #fff;
}

body.dark-layout .attendances-list .list-group-item:hover {
  background-color: #495057;
}

body.dark-layout .attendances-list .list-group-item.active {
  background-color: #1e3a8a;
  border-color: #3b82f6;
}

body.dark-layout .messages-section {
  border-top-color: #495057;
}

body.dark-layout .chat-historico {
  background-color: #38415f;
}

body.dark-layout .bubble-cliente {
  background-color: #3b4253;
  color: #d0d2d6;
}

body.dark-layout .bubble-cliente::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13'%3E%3Cpath fill='%233b4253' d='M2.812,0H8v11.193L1.533,2.568C0.474,1.156,1.042,0,2.812,0z'/%3E%3C/svg%3E");
}

body.dark-layout .bubble-atendente {
  background-color: #5a3d59;
  color: #e0d0df;
}

body.dark-layout .bubble-atendente::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13'%3E%3Cpath fill='%235a3d59' d='M5.188,0H0v11.193l6.467-8.625C7.526,1.156,6.958,0,5.188,0z'/%3E%3C/svg%3E");
}

body.dark-layout .bubble-bot {
  background-color: #4a4328;
  color: #e0d0c0;
  border-left-color: #FFA000;
}

body.dark-layout .bot-meta,
body.dark-layout .bot-timestamp {
  color: rgba(255, 255, 255, 0.5);
}

body.dark-layout .audio-player-ui {
  background-color: #3b4253;
  border-color: #495057;
}

/* Resumo de atendentes */
.resumo-atendentes {
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #9A3092;
}

.badge-atendente {
  background-color: #e9ecef;
  color: #495057;
  font-weight: 500;
}

body.dark-layout .resumo-atendentes {
  background: #2c3e50;
}

body.dark-layout .badge-atendente {
  background-color: #3b4253;
  color: #d0d2d6;
}

</style>
